import { useMemo } from 'react';

import { cn } from '@/lib/utils';
import { CATEGORY, CATEGORY_ORDER, MONTHS, WEEKDAY_INITIALS } from '../constants';
import { addDays, monthCells, monthsBetween, todayISO } from '../utils/dates';
import type { CalendarItemCategory, CalendarEventItem } from '../types/calendar';

export interface YearGridProps {
  /** The year the session opens in — 2026 for 2026–27. */
  /** The session's own ends — the grid draws every month between them. */
  from: string;
  to: string;
  events: CalendarEventItem[];
  /** Open one day in the month view. */
  onOpenDay: (iso: string) => void;
}

/**
 * The kind an entry is marked as, when a day carries more than one.
 *
 * Ranked by `CATEGORY_ORDER`, so a day that is both an exam day and a school
 * event is ringed as an exam — the ring has room for one colour and the reader
 * needs the more consequential of the two.
 */
function markFor(events: CalendarEventItem[]): Map<string, CalendarItemCategory> {
  const marks = new Map<string, CalendarItemCategory>();

  for (const item of events) {
    for (let iso = item.startDate; iso <= item.endDate; iso = addDays(iso, 1)) {
      const current = marks.get(iso);
      const isStronger =
        !current || CATEGORY_ORDER.indexOf(item.category) < CATEGORY_ORDER.indexOf(current);
      if (isStronger) marks.set(iso, item.category);
    }
  }

  return marks;
}

/**
 * Group events by ISO date string for quick tooltip lookup.
 */
function groupEventsByDate(events: CalendarEventItem[]): Map<string, CalendarEventItem[]> {
  const map = new Map<string, CalendarEventItem[]>();

  for (const item of events) {
    for (let iso = item.startDate; iso <= item.endDate; iso = addDays(iso, 1)) {
      const list = map.get(iso) ?? [];
      list.push(item);
      map.set(iso, list);
    }
  }

  return map;
}

/** One month of the session, small enough that twelve of them fit on a screen. */
function MiniMonth({
  year,
  month,
  marks,
  eventsByDate,
  today,
  onOpenDay,
}: {
  year: number;
  month: number;
  marks: Map<string, CalendarItemCategory>;
  eventsByDate: Map<string, CalendarEventItem[]>;
  today: string;
  onOpenDay: (iso: string) => void;
}) {
  const stamp = `${year}-${String(month + 1).padStart(2, '0')}`;
  const cells = monthCells(year, month);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] font-black tracking-tight text-ink">
        {MONTHS[month]} <span className="font-mono text-[11px] text-ink-3">{year}</span>
      </p>

      <div className="grid grid-cols-7 gap-y-1">
        {WEEKDAY_INITIALS.map((initial, index) => (
          <span
            key={`${initial}-${index}`}
            className="text-center text-[10px] font-black uppercase text-ink-3"
            aria-hidden
          >
            {initial}
          </span>
        ))}

        {cells.map((iso) => {
          // The leading and trailing cells belong to the months either side.
          // A mini month shows them empty rather than greyed, so twelve grids
          // read as twelve months and not as one continuous strip.
          if (!iso.startsWith(stamp)) return <span key={iso} aria-hidden />;

          const category = marks.get(iso);
          const dayEvents = eventsByDate.get(iso);
          const isToday = iso === today;
          const day = Number(iso.slice(8, 10));

          if (!category || !dayEvents || dayEvents.length === 0) {
            return (
              <span
                key={iso}
                className={cn(
                  'mx-auto grid h-6 w-6 place-items-center rounded-full text-[11px] font-semibold tabular-nums',
                  isToday ? 'bg-accent font-black text-white' : 'text-ink-2',
                )}
              >
                {day}
              </span>
            );
          }

          const style = CATEGORY[category];

          return (
            <div key={iso} className="relative group flex justify-center">
              <button
                type="button"
                onClick={() => onOpenDay(iso)}
                aria-label={`${day} ${MONTHS[month]} ${year} — ${style.label}`}
                className={cn(
                  'grid h-6 w-6 cursor-pointer place-items-center rounded-full border text-[11px] font-black tabular-nums transition-transform hover:scale-110 relative z-10',
                  isToday ? 'bg-accent text-white shadow-xs' : style.ring,
                )}
              >
                {day}
              </button>

              {/* Hover Tooltip Popup */}
              <div
                className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 hidden group-hover:flex flex-col items-center z-30"
                style={{ transform: 'translateX(-50%)' }}
              >
                <div className="flex flex-col items-center animate-pop-in">
                  <div className="rounded-xl border border-slate-800 dark:border-slate-700 bg-slate-900/95 dark:bg-slate-900/95 px-2.5 py-1.5 text-white shadow-xl backdrop-blur-md w-max max-w-[200px]">
                    <div className="flex flex-col gap-1">
                      {dayEvents.map((ev) => {
                        const catStyle = CATEGORY[ev.category];
                        return (
                          <div key={ev.uuid} className="flex items-center gap-1.5 text-[11.5px] leading-tight">
                            <span
                              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', catStyle.dot)}
                              aria-hidden
                            />
                            <span className="font-bold text-slate-100 truncate">{ev.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Arrow centered below card */}
                  <span className="-mt-1 h-2 w-2 rotate-45 bg-slate-900/95 border-b border-r border-slate-800 dark:border-slate-700 shrink-0" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The whole session at once, month by month. */
export function YearGrid({ from, to, events, onOpenDay }: YearGridProps) {
  const marks = useMemo(() => markFor(events), [events]);
  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);
  const today = todayISO();

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {monthsBetween(from, to).map(({ year, month }) => (
        <MiniMonth
          key={`${year}-${month}`}
          year={year}
          month={month}
          marks={marks}
          eventsByDate={eventsByDate}
          today={today}
          onOpenDay={onOpenDay}
        />
      ))}
    </div>
  );
}
